import { IonCard, IonCol, IonItem, IonLabel, IonModal, IonRow, IonThumbnail, IonGrid, IonIcon, IonBadge } from '@ionic/react';
import ImageViewer from './ImageViewer';
import { useState } from 'react';
import useNavigation from '../hooks/useNavigation';
import { calendarOutline, personOutline, businessOutline, locationOutline, call, chatbubbleOutline, imageOutline } from 'ionicons/icons';

interface IProps {
    salesActivity: any;
}

const SalesActivityCard: React.FC<IProps> = (props) => {

    const [selectedImage, setSelectedImage] = useState('');
    const [showImageModal, setShowImageModal] = useState(false);
    const navigateTo = useNavigation();

    const getGroupColor = (group: string) => {
        switch (group) {
            case 'agronomy': return 'success';
            case 'sales': return 'primary';
            case 'marketing': return 'warning';
            case 'service': return 'medium';
            default: return 'primary';
        }
    };

    const getGroupDisplayName = (group: string) => {
        switch (group) {
            case 'agronomy': return 'Agronomy';
            case 'sales': return 'Sales';
            case 'marketing': return 'Marketing';
            case 'service': return 'Service';
            default: return group;
        }
    };
    
    return (
        <>
            <IonCard onClick={() => {
                navigateTo("/tabs/salesActivity", { salesActivity: props.salesActivity })
            }} style={{ margin: '8px', borderRadius: '12px' }}>
                {/* Header with Activity Type and Actions */}
                <IonItem style={{ '--background': 'transparent', '--border-color': 'transparent' }}>
                    <IonLabel>
                        <h2 style={{ fontWeight: 'bold', fontSize: '16px', margin: '0' }}>
                            {props.salesActivity.salesActivityType?.description || 'Untitled Activity'}
                        </h2>
                    </IonLabel>
                </IonItem>

                {/* Main Content Grid */}
                <IonGrid style={{ padding: '0 16px 16px 16px' }}>
                    <IonRow>
                        {/* Left Column - Basic Info */}
                        <IonCol size="12" sizeMd="6">
                            {/* Group Tag */}
                            {props.salesActivity.group && (
                                <div style={{ marginBottom: '12px' }}>
                                    <IonBadge 
                                        color={getGroupColor(props.salesActivity.group)} 
                                        style={{ fontSize: '12px' }}
                                    >
                                        {getGroupDisplayName(props.salesActivity.group)}
                                    </IonBadge>
                                </div>
                            )}

                            {/* Date & Time */}
                            <div style={{ marginBottom: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <IonIcon icon={calendarOutline} style={{ fontSize: '16px', marginRight: '8px', color: '#666' }} />
                                    <span style={{ fontSize: '14px' }}>
                                        {new Intl.DateTimeFormat('en-GB', {
                                            month: 'short',
                                            day: '2-digit',
                                            year: 'numeric',
                                            hour: 'numeric',
                                            minute: '2-digit',
                                            hour12: true
                                        }).format(new Date(props.salesActivity.date))}
                                    </span>
                                </div>
                            </div>

                            {/* Location */}
                            {props.salesActivity.place && (
                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <IonIcon icon={locationOutline} style={{ fontSize: '16px', marginRight: '8px', color: '#666' }} />
                                        <span style={{ fontSize: '14px' }}>
                                            {props.salesActivity.place}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Users */}
                            {props.salesActivity?.userIds && props.salesActivity.userIds.length > 0 && (
                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <IonIcon icon={personOutline} style={{ fontSize: '16px', marginRight: '8px', color: '#666' }} />
                                        <span style={{ fontSize: '14px' }}>
                                            {props.salesActivity.userIds.map((item: any) => item.name).join(', ')}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Contact */}
                            {props.salesActivity.contact && props.salesActivity.contact.length > 0 && (
                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <IonIcon icon={call} style={{ fontSize: '16px', marginRight: '8px', color: '#666' }} />
                                        <span style={{ fontSize: '14px' }}>
                                            {props.salesActivity.contact}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </IonCol>

                        {/* Right Column - Additional Info */}
                        <IonCol size="12" sizeMd="6">
                            {/* Serviced By */}
                            {props.salesActivity.servicedBy && props.salesActivity.servicedBy.length > 0 && (
                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <IonIcon icon={personOutline} style={{ fontSize: '16px', marginRight: '8px', color: '#666' }} />
                                        <span style={{ fontSize: '14px' }}>
                                            {props.salesActivity.servicedBy}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Comment */}
                            {props.salesActivity.comment && props.salesActivity.comment.length > 0 && (
                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                        <IonIcon icon={chatbubbleOutline} style={{ fontSize: '16px', marginRight: '8px', marginTop: '2px', color: '#666' }} />
                                        <span style={{ fontSize: '14px', lineHeight: '1.4' }}>
                                            {props.salesActivity.comment}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Attachments */}
                            {props.salesActivity.attachments?.thumbnails && props.salesActivity.attachments.thumbnails.length > 0 && (
                                <div style={{ marginBottom: '12px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <IonIcon icon={imageOutline} style={{ fontSize: '16px', marginRight: '8px', color: '#666' }} />
                                        <span style={{ fontSize: '14px' }}>
                                            {props.salesActivity.attachments.thumbnails.length} image(s)
                                        </span>
                                    </div>
                                </div>
                            )}
                        </IonCol>
                    </IonRow>

                    {/* Image Thumbnails Row */}
                    {props.salesActivity.attachments?.thumbnails && props.salesActivity.attachments.thumbnails.length > 0 && (
                        <IonRow>
                            <IonCol>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    {props.salesActivity.attachments.thumbnails.map((item: any, index: number) => (
                                        <div key={index} style={{ cursor: 'pointer' }}>
                                            <IonThumbnail style={{ width: '60px', height: '60px' }}>
                                                <img 
                                                    src={item} 
                                                    alt='activityImage' 
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        setSelectedImage(props.salesActivity.attachments?.images[index]);
                                                        setShowImageModal(true);
                                                    }}
                                                    style={{ borderRadius: '8px' }}
                                                />
                                            </IonThumbnail>
                                        </div>
                                    ))}
                                </div>
                            </IonCol>
                        </IonRow>
                    )}
                </IonGrid>

                <IonModal isOpen={showImageModal} cssClass='my-custom-class'>
                    <ImageViewer imageUrl={selectedImage} onDeleteHandler={()=>{}} onCloseHandler={setShowImageModal} />
                </IonModal>
            </IonCard>
        </>
    )
};

export default SalesActivityCard;